import { http } from '@/lib/http';

/**
 * Tenant-owned outbound email — per-tenant sending identities.
 *
 * Backs Settings → Email senders. A tenant configures + operates their OWN
 * sender (own domain via SES, or their own SMTP server); the platform never
 * owns this function. Maps to the runtime `tenant-email/identities` API.
 */
const BASE = '/api/v1/tenant-email/identities';

export type EmailTransport =
  | 'SES_DELEGATED'
  | 'SMTP'
  | 'GMAIL_OAUTH'
  | 'M365_OAUTH';

export type EmailIdentityStatus =
  | 'PENDING'
  | 'ACTIVE'
  | 'FAILED'
  | 'DISCONNECTED';

export type EmailDomainStatus = 'NONE' | 'PENDING' | 'VERIFIED' | 'FAILED';

export interface DnsRecord {
  type: 'TXT' | 'CNAME';
  name: string;
  value: string;
  purpose: string;
  required: boolean;
}

export interface SmtpCredentials {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  pass: string;
}

export interface EmailIdentity {
  id: string;
  tenantId: string;
  organizationId: string | null;
  label: string;
  transport: EmailTransport;
  status: EmailIdentityStatus;
  isDefault: boolean;
  fromAddress: string;
  fromName: string | null;
  replyTo: string | null;
  domain: string | null;
  domainStatus: EmailDomainStatus;
  dnsRecords: DnsRecord[] | null;
  lastCheckedAt: string | null;
  lastTestedAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
  hasCredentials: boolean;
}

export interface CreateIdentityPayload {
  label: string;
  transport: EmailTransport;
  fromAddress: string;
  fromName?: string;
  replyTo?: string;
  organizationId?: string;
  domain?: string;
  smtp?: SmtpCredentials;
  isDefault?: boolean;
}

export interface UpdateIdentityPayload {
  label?: string;
  fromName?: string;
  replyTo?: string;
  isDefault?: boolean;
  smtp?: SmtpCredentials;
}

export const tenantEmailService = {
  list: () => http.get<EmailIdentity[]>(BASE),
  get: (id: string) => http.get<EmailIdentity>(`${BASE}/${id}`),
  create: (payload: CreateIdentityPayload) =>
    http.post<EmailIdentity, CreateIdentityPayload>(BASE, payload),
  update: (id: string, payload: UpdateIdentityPayload) =>
    http.patch<EmailIdentity, UpdateIdentityPayload>(`${BASE}/${id}`, payload),
  remove: (id: string) => http.delete<void>(`${BASE}/${id}`),
  setDefault: (id: string) =>
    http.post<EmailIdentity>(`${BASE}/${id}/default`),
  dns: (id: string) => http.get<DnsRecord[]>(`${BASE}/${id}/dns`),
  refreshDomain: (id: string) =>
    http.post<EmailIdentity>(`${BASE}/${id}/refresh-domain`),
  test: (id: string, to: string) =>
    http.post<{ ok: true; messageId: string }, { to: string }>(
      `${BASE}/${id}/test`,
      { to },
    ),
};
